import type { UseStore } from 'idb-keyval';
import { clear, createStore, entries, set } from 'idb-keyval';
import type { GetClusterRouteQuery } from '../../graphql';
import type { Distances, RouteParams } from './context';
import { parseRoute, routeParamsToString } from './distance-utils';
import type { ScenarioLocation } from '../../lib/planningData';
import type { MutableRefObject } from 'react';
import { DistanceMap } from './distanceMap';
import type { ClusterId, LocationId } from '@seaber/common/core/entries';

let store: UseStore;

export const generateIdbKey = (
  pair: RouteParams,
  draft: number | null,
  noGoZones: string,
) => {
  const fromClusterId = pair[0];
  const toClusterId = pair[1];

  if (!fromClusterId || !toClusterId) {
    return;
  }

  return routeParamsToString([
    fromClusterId,
    toClusterId,
    pair[2],
    pair[3] ?? draft,
    pair[4] ?? noGoZones ?? null,
  ]);
};

export function getDistanceIdb(): UseStore {
  // Update version if we need to clear the browser cache for users
  if (!store) store = createStore('distance-cache-11', 'keyval');
  return store;
}

export async function clearDistanceCache(): Promise<void> {
  return await clear(getDistanceIdb());
}

export const getStoredDistances = async (
  draft: number | null,
  locationsById: Map<LocationId, ScenarioLocation>,
  distances: MutableRefObject<Distances>,
  noGoZones: string,
): Promise<Distances> => {
  const distanceEntries = await entries(getDistanceIdb());
  const storedDistances = new DistanceMap(
    distances.current.map,
    locationsById,
    draft,
    noGoZones,
    distances.current.overrides,
  );
  const map = storedDistances.map;

  for (const [key, value] of distanceEntries) {
    if (!value || !key) continue;

    const [
      fromClusterIdStr,
      toClusterIdStr,
      avoidChannelsStr,
      draftStr,
      noGoZones,
    ] = key.toString().split('_');
    const cachedDraft = draftStr !== 'null' ? +draftStr : null;

    try {
      const distance = parseRoute(value);
      map.set(
        distance,
        +fromClusterIdStr as ClusterId,
        +toClusterIdStr as ClusterId,
        JSON.parse(avoidChannelsStr),
        cachedDraft,
        noGoZones,
      );
    } catch (err) {
      console.error('route parsing failed', err);
    }
  }

  return storedDistances;
};

export const setStoredDistance = (
  params: RouteParams,
  draft: number | null,
  data: GetClusterRouteQuery,
  noGoZones: string,
) => {
  const idbKey = generateIdbKey(params, draft, noGoZones);
  if (idbKey) {
    void set(idbKey, data, getDistanceIdb());
  }
};
